import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { usePlayer } from './PlayerContext';
import logger from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const RadioContext = createContext(null);

export const useRadio = () => {
  const context = useContext(RadioContext);
  if (!context) {
    throw new Error('useRadio must be used within a RadioProvider');
  }
  return context;
};

export const RadioProvider = ({ children }) => {
  const { registerRadioCallbacks } = usePlayer();
  
  const [radioEnabled, setRadioEnabled] = useState(false); // Admin toggle
  const [radioTracks, setRadioTracks] = useState([]);
  const [currentRadioTrack, setCurrentRadioTrack] = useState(null);
  const [radioIndex, setRadioIndex] = useState(0);
  const [isRadioPlaying, setIsRadioPlaying] = useState(false);
  const [radioMuted, setRadioMuted] = useState(true); // Start muted for mobile
  const [radioVolume, setRadioVolume] = useState(0.5);
  const [showTuneInPrompt, setShowTuneInPrompt] = useState(true); // Mobile tap-to-tune-in
  const [radioPausedByUser, setRadioPausedByUser] = useState(false); // Track if paused by user playing other track

  const radioAudioRef = useRef(new Audio());

  // Fetch radio settings and tracks on mount
  useEffect(() => {
    const fetchRadioSettings = async () => {
      try {
        const [settingsRes, tracksRes] = await Promise.all([
          axios.get(`${API}/api/radio/settings`),
          axios.get(`${API}/api/radio/tracks`)
        ]);

        setRadioEnabled(settingsRes.data.enabled || false);
        
        if (tracksRes.data && tracksRes.data.length > 0) {
          // Shuffle tracks
          const shuffled = [...tracksRes.data].sort(() => Math.random() - 0.5);
          setRadioTracks(shuffled);
          setCurrentRadioTrack(shuffled[0]);
          setRadioIndex(0);
        }
      } catch (err) {
        logger.error('Failed to load radio settings:', err);
      }
    };

    fetchRadioSettings();
  }, []);

  // Load and play radio track
  useEffect(() => {
    if (!currentRadioTrack || !radioEnabled) return;
    // Skip tracks that don't have an audio file yet (e.g. Shopify-imported
    // tracks where the artist hasn't uploaded the audio to R2). Bulk-imports
    // can land tracks with null audio_url; we just skip past them.
    if (!currentRadioTrack.audio_url) {
      playNextRadioTrack();
      return;
    }

    const audio = radioAudioRef.current;
    // Ensure full URL for audio source
    const audioUrl = currentRadioTrack.audio_url.startsWith('http') 
      ? currentRadioTrack.audio_url 
      : `${API}${currentRadioTrack.audio_url}`;
    audio.src = audioUrl;
    audio.volume = radioMuted ? 0 : radioVolume;
    audio.load();

    const handleEnded = () => {
      // Auto-advance to next track
      playNextRadioTrack();
    };

    const handleError = (e) => {
      logger.error('Radio audio error:', e);
      // Skip to next track on error
      playNextRadioTrack();
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Auto-play if radio is playing and not paused by user
    if (isRadioPlaying && !radioPausedByUser) {
      audio.play().catch(err => {
        logger.debug('Radio autoplay blocked (expected on mobile):', err);
        // Keep showing tune-in prompt
      });
    }

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [currentRadioTrack, radioEnabled, radioMuted, radioVolume, isRadioPlaying, radioPausedByUser]);

  // Play next track in shuffle
  const playNextRadioTrack = useCallback(() => {
    if (radioTracks.length === 0) return;

    let nextIndex = radioIndex + 1;
    
    // If reached end, reshuffle and start over
    if (nextIndex >= radioTracks.length) {
      const reshuffled = [...radioTracks].sort(() => Math.random() - 0.5);
      setRadioTracks(reshuffled);
      setRadioIndex(0);
      setCurrentRadioTrack(reshuffled[0]);
    } else {
      setRadioIndex(nextIndex);
      setCurrentRadioTrack(radioTracks[nextIndex]);
    }
  }, [radioTracks, radioIndex]);

  // Toggle radio play/pause
  const toggleRadioPlay = useCallback(() => {
    const audio = radioAudioRef.current;
    
    if (isRadioPlaying) {
      audio.pause();
      setIsRadioPlaying(false);
    } else {
      setShowTuneInPrompt(false); // Hide prompt once user interacts
      audio.play().then(() => {
        setIsRadioPlaying(true);
      }).catch(err => {
        logger.error('Radio play failed:', err);
      });
    }
  }, [isRadioPlaying]);

  // Toggle radio mute
  const toggleRadioMute = useCallback(() => {
    const audio = radioAudioRef.current;
    const newMuted = !radioMuted;
    setRadioMuted(newMuted);
    audio.volume = newMuted ? 0 : radioVolume;
    
    // Hide tune-in prompt when user unmutes
    if (!newMuted) {
      setShowTuneInPrompt(false);
    }
  }, [radioMuted, radioVolume]);

  // Pause radio (called by main player when user plays a track)
  const pauseRadio = useCallback(() => {
    const audio = radioAudioRef.current;
    audio.pause();
    setIsRadioPlaying(false);
    setRadioPausedByUser(true);
  }, []);

  // Resume radio (called by main player when user stops their track)
  const resumeRadio = useCallback(() => {
    if (!radioEnabled || !currentRadioTrack) return;
    
    const audio = radioAudioRef.current;
    setRadioPausedByUser(false);
    audio.play().then(() => {
      setIsRadioPlaying(true);
    }).catch(err => {
      logger.debug('Radio resume blocked:', err);
    });
  }, [radioEnabled, currentRadioTrack]);

  // Update radio enabled state (for admin)
  const updateRadioEnabled = useCallback((enabled) => {
    setRadioEnabled(enabled);
    
    if (!enabled) {
      const audio = radioAudioRef.current;
      audio.pause();
      setIsRadioPlaying(false);
    }
  }, []);

  // Register pause/resume callbacks with PlayerContext
  useEffect(() => {
    registerRadioCallbacks(pauseRadio, resumeRadio);
  }, [registerRadioCallbacks, pauseRadio, resumeRadio]);

  const value = {
    radioEnabled,
    currentRadioTrack,
    isRadioPlaying,
    radioMuted,
    radioVolume,
    showTuneInPrompt,
    toggleRadioPlay,
    toggleRadioMute,
    pauseRadio,
    resumeRadio,
    updateRadioEnabled,
    radioAudioRef
  };

  return (
    <RadioContext.Provider value={value}>
      {children}
    </RadioContext.Provider>
  );
};
